import React from 'react';
import ReactDatePicker from 'react-datepicker';
import { cn } from '../utils/cn';
import { Calendar } from 'lucide-react';

// Import react-datepicker CSS
import 'react-datepicker/dist/react-datepicker.css';

/**
 * DatePicker Component - Atom
 * Modern date picker using react-datepicker
 * 
 * @param {Object} props
 * @param {Date|string|null} props.selected - Selected date
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholderText - Placeholder text
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {string} props.variant - 'default' | 'error' | 'success'
 */
const DatePicker = React.forwardRef(
  (
    {
      selected,
      onChange,
      placeholderText = 'Select date',
      disabled = false,
      size = 'md',
      variant = 'default',
      className = '',
      ...props
    },
    ref
  ) => {
    const baseStyles = 'w-full rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
      default: 'border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-800 text-primary-900 dark:text-primary-50 placeholder-primary-400 dark:placeholder-primary-500 focus:border-primary-500 dark:focus:border-primary-400 focus:ring-primary-500',
      error: 'border-error-400 dark:border-error-600 bg-error-50 dark:bg-error-900/30 text-error-900 dark:text-error-50 focus:border-error-500 dark:focus:border-error-500 focus:ring-error-500',
      success: 'border-success-400 dark:border-success-600 bg-success-50 dark:bg-success-900/30 text-success-900 dark:text-success-50 focus:border-success-500 dark:focus:border-success-500 focus:ring-success-500',
    };

    const sizes = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-2.5 text-base',
      lg: 'px-5 py-3 text-lg',
    };

    const dateValue = (() => {
      if (!selected) return null;
      // Robust Date check
      if (selected && typeof selected.getTime === 'function') {
        // Check if date is valid
        return isNaN(selected.getTime()) ? null : selected;
      }
      if (typeof selected === 'string' && selected !== '') {
        const date = new Date(selected);
        // Check if date is valid
        return isNaN(date.getTime()) ? null : date;
      }
      return null;
    })();

    const handleChange = (date) => {
      if (onChange) {
        onChange(date);
      }
    };

    return (
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none">
          <Calendar className="text-primary-400 dark:text-primary-500" size={18} />
        </div>
        <ReactDatePicker
          ref={ref}
          selected={dateValue}
          onChange={handleChange}
          disabled={disabled}
          placeholderText={placeholderText}
          dateFormat="dd/MM/yyyy"
          className={cn(
            baseStyles,
            variants[variant],
            sizes[size],
            'pl-10',
            className
          )}
          wrapperClassName="w-full"
          calendarClassName="!rounded-xl !border-2 !border-primary-200 dark:!border-primary-700 !shadow-lg !bg-white dark:!bg-primary-800"
          dayClassName={(date) =>
            cn(
              '!rounded-lg hover:!bg-secondary-100 dark:hover:!bg-secondary-900/50',
              date && dateValue && date.toDateString() === dateValue.toDateString() && '!bg-secondary-500 !text-white'
            )
          }
          {...props}
        />
      </div>
    );
  }
);

DatePicker.displayName = 'DatePicker';

export default DatePicker;
